import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { toast } from "react-toastify";
import { Toast } from "../../utils/helper";
import EditorToolbar from "./EditorToolbar";

const RichTextEditor = forwardRef(
  ({ value = "", onChange, maxLength, maxLengthMessage }, ref) => {
    const lastValidHtmlRef = useRef(
      value || "<p>Start typing your content here...</p>",
    );
    const didShowLimitToastRef = useRef(false);
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          paragraph: {
            HTMLAttributes: {
              class: "tiptap-paragraph",
            },
          },
        }),
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Link.configure({
          autolink: true,
          linkOnPaste: true,
          protocols: ["http", "https", "mailto"],
        }),
        Image.configure({
          allowBase64: true,
          HTMLAttributes: { class: "img-fluid rounded mb-3" },
        }),
      ],
      content: value || "<p>Start typing your content here...</p>",
      editorProps: {
        attributes: {
          class: "prose prose-sm focus:outline-none p-3 min-h-[400px]",
          style: "line-height: 1.6; white-space: pre-wrap;",
        },
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const plainText = editor.getText() || "";
        const hasLimit = Number.isFinite(maxLength) && maxLength > 0;

        if (hasLimit && plainText.length > maxLength) {
          if (!didShowLimitToastRef.current) {
            toast.error(
              <Toast
                msg={
                  maxLengthMessage ||
                  `Maximum length is ${maxLength} characters`
                }
              />,
            );
            didShowLimitToastRef.current = true;
          }
          editor.commands.setContent(lastValidHtmlRef.current || "", false);
          return;
        }

        didShowLimitToastRef.current = false;
        lastValidHtmlRef.current = html;
        onChange?.(html);
      },
    });

    useImperativeHandle(ref, () => ({
      insertVariable: (variable) => {
        if (!editor) return;
        editor.chain().focus().insertContent(`[${variable}]`).run();
      },
      getHTML: () => editor?.getHTML(),
    }));

    useEffect(() => {
      if (editor && value !== editor.getHTML()) {
        editor.commands.setContent(value || "", false);
        lastValidHtmlRef.current = value || "";
      }
    }, [value, editor]);

    if (!editor) return null;

    return (
      <div ref={ref}>
        <div className="card border">
          <EditorToolbar editor={editor} isCodeActive={false} />
          <div
            className="bg-white p-2"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxHeight: "350px", overflowY: "auto" }}
            s
          >
            <EditorContent editor={editor} />
          </div>
        </div>

        <style>{`
        .ProseMirror {
          min-height: 400px;
          padding: 1rem;
          outline: none;
          line-height: 1.6;
          white-space: pre-wrap;
        }
        .ProseMirror p {
          margin: 0 0 1rem;
        }
        .ProseMirror p:empty::before {
          content: "\\00a0";
        }
      `}</style>
      </div>
    );
  },
);

export default RichTextEditor;
